/**
 * One per world scene (mounted by MultiplayerSync, inside the Canvas):
 *  - listens on the world channel for "a block changed" pings, so mutual
 *    blocks apply live on the other person's screen too;
 *  - first-person (pointer-locked) desktop mode: a click aims at the screen
 *    centre, so it raycasts the player hit columns from there;
 *  - in VR, shows the safety panel in front of you when one is open.
 */
import React, { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { getChannel } from '../../services/realtimeManager';
import { attachSafetyChannel } from '../../services/safety';
import { useSafetyUi } from '../../stores/safetyUiStore';
import { playerHitTargets } from './PlayerSafetyGate';
import { openPlayerCard } from './openSafety';
import VRPlayerCard from './VRPlayerCard';

const _center = new THREE.Vector2(0, 0);
const _rc = new THREE.Raycaster();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _fwd = new THREE.Vector3();

function VRSafetyHost() {
  const view = useSafetyUi((s) => s.vrView);
  const openedAt = useSafetyUi((s) => s.vrOpenedAt);
  const inXR = useXR((s) => !!s.session);
  const camera = useThree((s) => s.camera);
  const group = useRef<THREE.Group>(null);
  const placedAt = useRef(-1);

  // Session over: close the panel.
  useEffect(() => { if (!inXR) useSafetyUi.setState({ vrView: null }); }, [inXR]);

  // Anchor in front of the head each time it's opened; then it stays put in
  // the world (like the share panel) so it doesn't swim with every glance.
  useFrame(() => {
    const g = group.current;
    if (!g || !view) return;
    if (placedAt.current !== openedAt) {
      camera.getWorldPosition(_pos);
      camera.getWorldQuaternion(_quat);
      _fwd.set(0, 0, -1).applyQuaternion(_quat);
      _fwd.y = 0;
      if (_fwd.lengthSq() < 1e-4) _fwd.set(0, 0, -1);
      _fwd.normalize();
      // Closer than the VR menu (0.9 m) so it sits in front of it.
      _pos.addScaledVector(_fwd, 0.62);
      _pos.y -= 0.1;
      g.position.copy(g.parent ? g.parent.worldToLocal(_pos) : _pos);
      g.rotation.set(-0.12, Math.atan2(-_fwd.x, -_fwd.z), 0, 'YXZ');
      placedAt.current = openedAt;
      g.visible = true;
    } else {
      // Walked away from it: close.
      camera.getWorldPosition(_pos);
      if (_pos.distanceTo(g.getWorldPosition(_fwd)) > 3.5) useSafetyUi.setState({ vrView: null });
    }
  });

  if (!inXR || !view) return null;
  return <group ref={group} name="vr-safety-panel" visible={false}><VRPlayerCard view={view} /></group>;
}

export default function SafetyWorldHost({ worldId }: { worldId: string }) {
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  useEffect(() => {
    if (!worldId) return;
    getChannel(worldId); // same channel MultiplayerSync uses (idempotent)
    attachSafetyChannel();
  }, [worldId]);

  useEffect(() => {
    const el = gl.domElement;
    const onClick = () => {
      if (document.pointerLockElement !== el || playerHitTargets.size === 0) return;
      _rc.setFromCamera(_center, cameraRef.current);
      _rc.far = 25;
      let best: { d: number; key: string } | null = null;
      for (const [key, t] of playerHitTargets) {
        const h = _rc.intersectObject(t.mesh, false)[0];
        if (h && (!best || h.distance < best.d)) best = { d: h.distance, key };
      }
      if (!best) return;
      const t = playerHitTargets.get(best.key)!;
      openPlayerCard({ userId: t.userId, key: best.key, name: t.name });
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [gl]);

  return <VRSafetyHost />;
}
